import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { RightClickMenuConfirm, RightClickMenuItem, RightClickMenuItemItem, RightClickMenuPopper } from "./RightClickMenu";
import { v4 as uuidv4 } from "uuid";

export interface RightClickMenuContextValue {
  openMenu: (x: number, y: number, menuItems: RightClickMenuItem[]) => void;
  closeMenu: () => void;
}

const defaultRightClickMenuContext: RightClickMenuContextValue = {
  openMenu: () => { },
  closeMenu: () => { },
}

interface RightClickMenuState {
  x: number;
  y: number;
  uniqueId: string;
  menuItems: RightClickMenuItem[];
}

const RightClickMenuContext = createContext<RightClickMenuContextValue>(defaultRightClickMenuContext);

export const RightClickMenuContextProvider = (props: React.PropsWithChildren<any>) => {
  const [state, setState] = useState<RightClickMenuState>({
    x: 0,
    y: 0,
    uniqueId: null,
    menuItems: [],
  });
  const [confirmingItem, setConfirmingItem] = useState<RightClickMenuItemItem>(null);
  const pointerDivRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", closeMenu);
    document.addEventListener("contextmenu", documentOnContextMenu);
    document.addEventListener("scroll", documentOnScroll);
    window.addEventListener("resize", documentOnResize);
    document.addEventListener("keydown", documentOnEscape);
    return () => {
      document.removeEventListener("click", closeMenu);
      document.removeEventListener("contextmenu", documentOnContextMenu);
      document.removeEventListener("scroll", documentOnScroll);
      window.removeEventListener("resize", documentOnResize);
      document.removeEventListener("keydown", documentOnEscape);
    };
  }, []);

  const openMenu = (x: number, y: number, menuItems: RightClickMenuItem[]) => {
    setState({ x, y, uniqueId: uuidv4(), menuItems });
  };

  const closeMenu = useCallback((e?: MouseEvent) => {
    // Workaround for weird mac safari behavior
    if (e && e.ctrlKey && /mac/i.test(navigator.userAgent)) {
      return;
    }

    setState({ x: 0, y: 0, uniqueId: null, menuItems: [] });
  }, []);

  const documentOnEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      closeMenu();
    }
  }, [closeMenu]);

  const documentOnResize = useCallback(() => {
    setState((prev) => {
      if (!prev.uniqueId) {
        return prev;
      }
      return { x: 0, y: 0, uniqueId: null, menuItems: [] };
    });
  }, []);

  const documentOnScroll = useCallback(() => {
    setState((prev) => {
      if (!prev.uniqueId) {
        return prev;
      }
      return { x: 0, y: 0, uniqueId: null, menuItems: [] };
    });
  }, []);

  const documentOnContextMenu = useCallback((e: MouseEvent) => {
    if (e.target instanceof HTMLElement) {
      if (e.target.closest("[data-has-right-click-menu]")) {
        return;
      }
    }

    closeMenu();
  }, [closeMenu]);

  return <RightClickMenuContext.Provider value={{ openMenu, closeMenu }}>
    {state.uniqueId && <RightClickMenuPopper
      key={state.uniqueId}
      target={pointerDivRef.current}
      menuItems={state.menuItems}
      setConfirmingItem={item => setConfirmingItem(item)}
    />}
    <RightClickMenuConfirm
      confirmingItem={confirmingItem}
      closeConfirmingItem={() => setConfirmingItem(null)}
    />
    <div style={{
      // Add 1 pixel so if an element is hovered, it remains hovered when the menu is open.
      top: state.y + 1,
      left: state.x,
      position: "fixed",
      pointerEvents: "none",
    }} ref={pointerDivRef} />
    {props.children}
  </RightClickMenuContext.Provider>
}

interface RightClickMenuProps {
  menuItems: RightClickMenuItem[];
  openOnClick?: boolean;
}

export const RightClickMenu = (props: React.PropsWithChildren<RightClickMenuProps>) => {
  const context = useContext(RightClickMenuContext);

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    context.openMenu(e.clientX, e.clientY, props.menuItems);
  }

  const onClick = (e: React.MouseEvent) => {
    // The document click listener would close the menu right away
    e.stopPropagation();
    context.openMenu(e.clientX, e.clientY, props.menuItems);
  }

  return <div
    onContextMenu={onContextMenu}
    onClick={props.openOnClick ? onClick : undefined}
    data-has-right-click-menu="true"
    style={{ display: "contents" }}
  >
    {props.children}
  </div>
}